import { Dayjs } from 'dayjs'
import { useCallback } from 'react'

import {
  DOWNLOAD_CONFIG,
  DownloadRequestType,
} from '../tabs/devices/utils/downloadConfig'
import { DeviceResponseType } from './useDeviceRPC'

type Fetchers = {
  fetchLogs: (attachmentId: string) => Promise<Uint8Array[] | null>
  fetchBundle: (attachmentId: string) => Promise<Uint8Array[] | null>
  fetchFlamegraph: (attachmentId: string) => Promise<Uint8Array[] | null>
  fetchMetrics: (attachmentId: string) => Promise<Uint8Array[] | null>
}

type Options = {
  peerKey?: string
}

export default function useDownloadDeviceData(
  fetchers: Fetchers,
  options?: Options,
) {
  const { fetchLogs, fetchBundle, fetchFlamegraph, fetchMetrics } = fetchers
  const { peerKey } = options || {}

  return useCallback(
    (date: Dayjs, attachmentId: string | null, type: DeviceResponseType) =>
      async () => {
        if (!attachmentId) {
          return
        }

        const config = DOWNLOAD_CONFIG[type as DownloadRequestType]
        if (!config) {
          console.error(`Unknown request type: ${type}`)
          return
        }

        const fetcherMap = {
          fetchLogs,
          fetchBundle,
          fetchFlamegraph,
          fetchMetrics,
        }

        const fetchFn = fetcherMap[config.fetcher]
        const bytes = await fetchFn(attachmentId)

        if (!bytes) {
          return
        }

        const blob = new Blob(bytes as BlobPart[], { type: config.mimeType })
        const blobUrl = URL.createObjectURL(blob)
        const fileNameFriendlyDate = date.toISOString().replaceAll(':', '-')
        const peerKeyPart = peerKey ? `-${peerKey}` : ''
        const fileName = `small-peer-${type}${peerKeyPart}-${fileNameFriendlyDate}.${config.extension}`

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      },
    [fetchLogs, fetchBundle, fetchFlamegraph, fetchMetrics, peerKey],
  )
}
